import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, IconButton, Svg, Typography } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants';
import { useNotification } from '@/_shared/hooks';
import { getOrganizationsCall, putSetOrganizationCall } from '@/core/state/api';
import { Badge } from '../Badge';

type Props = {
  goBack?: () => void;
  hideGoBack?: boolean;
};

export const SwitchOrganization: FC<Props> = ({ goBack, hideGoBack }) => {
  const t = useTranslations('general.switchOrganization');
  const sharedT = useTranslations();
  const { notify } = useNotification();
  const [onMouseHover, setOnMouseHover] = useState<string | undefined>(undefined);
  const {
    execute: executeGetOrganizations,
    defaultOrganizationId,
    data,
    success: successGetOrganizations,
    error: errorGetOrganizations,
    loading: loadingGetOrganizations,
  } = getOrganizationsCall.hook();
  const { execute: executePutSetOrganization } = putSetOrganizationCall.hook();

  const onSwitch = useCallback(
    (orgaId: string, userId: string) => {
      if (orgaId !== defaultOrganizationId) {
        executePutSetOrganization({
          organization: orgaId,
          userId,
          successSaga: () => {
            notify(sharedT('success.genericApiSuccess'), 'success');
            setTimeout(() => {
              window.location.href = Routes.Homepage;
            }, 1000);
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
          },
        });
      }
    },
    [executePutSetOrganization, notify, sharedT, defaultOrganizationId],
  );

  useEffect(() => {
    if (!successGetOrganizations && !loadingGetOrganizations && !errorGetOrganizations) {
      executeGetOrganizations({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      bg='grey/dark/background'
      p='s'
      display='flex'
      flexDirection='column'
      borderRadius='l'
      boxShadow='0px 0px 16px 0px #00000066'
      minWidth={300}
      justifyContent='center'
      alignItems='center'
    >
      <Typography variant='p1' mt='xxs' textAlign='center' mb='s'>
        {t('title')}
      </Typography>
      {data.map((item, index) => (
        <Box key={item.organizationId} width='100%'>
          <Box
            p='xxs'
            display='flex'
            alignItems='center'
            alignContent='center'
            borderRadius='l'
            style={{ cursor: 'pointer' }}
            flex={1}
            width='100%'
            onClick={() => onSwitch(item.organizationId, item.userId)}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(onMouseHover === item.organizationId ? { backgroundColor: 'grey/dark' } : {})}
            onMouseEnter={() => setOnMouseHover(item.organizationId)}
            onMouseLeave={() => setOnMouseHover(undefined)}
          >
            <Svg name='company' size='xs' />
            <Typography ml='xxs' variant='p2/light' fontSize='xs' flex={1}>
              {item.name}
            </Typography>
            {item.organizationId === defaultOrganizationId && (
              <Badge
                label={t('nowLogged')}
                textProps={{ fontSize: 'xxxs', color: 'primary/text' }}
                ml='xs'
                py='none'
                px='xxs'
                opacity={0.9}
              />
            )}
          </Box>
          {index < data.length - 1 && (
            <Box height={1} backgroundColor='grey/light/background' width='100%' my='xxs' />
          )}
        </Box>
      ))}
      {!hideGoBack && (
        <Box display='flex' flexDirection='row' alignItems='center' mt='m'>
          <IconButton
            prefixSvg='back'
            variant='link/primary'
            onClick={goBack}
            style={{ textDecoration: 'none', cursor: 'pointer' }}
            svgStyle={{ width: 'xs' }}
            textStyle={{ variant: 'p2/light', marginLeft: 'xxxxs' }}
          >
            <Typography fontWeight='500' ml='xxs' as='span' data-testid='goBackCta'>
              {t('buttonGoBack')}
            </Typography>
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
